import { Button, ProgressBar } from "react-native-paper";
import React from "react";
import { DocumentReference, DocumentSnapshot } from "@atgof-firebase/types";
import { episodeForScene, getEpisodePath } from "../common/episode";
import {
  applicandCollectionName,
  ApplicandKind,
  episodePathForApplicand,
  kindOfApplicand,
  subjectRefForApplicand,
  userFacingApplicandKind,
} from "../common/applicand";
import { isCharacterCategory, SheetCategory } from "../common/sheet";
import { useAvailableApplicands } from "../data/useAvailableApplicands";
import { ChipSelector } from "./ChipSelector";
import { useProject } from "../data/useProject";
import { View } from "react-native";
import { LanguageContext } from "../common/language";
import FixtureLabel from "../common/FixtureLabel";
import { ApplicandChip } from "./ApplicandChip";
import { subjectRef } from "@atgof-common/subject";

export function ApplicandsSelector({
  applicandKind,
  doc,
  readonly,
  docIsApplicand,
}: {
  applicandKind: ApplicandKind;
  doc: DocumentSnapshot;
  readonly?: boolean;
  docIsApplicand?: boolean;
}) {
  const { ph } = React.useContext(LanguageContext);
  const project = useProject();
  const category: SheetCategory | undefined = doc.get("category");
  const subjectKind = isCharacterCategory(category) ? "character" : "set";
  const subjectName: string | undefined = doc.get(
    isCharacterCategory(category) ? "character" : "set",
  );
  const parentApplicandKind = docIsApplicand
    ? kindOfApplicand(doc.ref)
    : undefined;
  const applicandSubjectRef: DocumentReference | undefined =
    (subjectName
      ? subjectRef(project, { subjectKind, subjectName })
      : undefined) ??
    (docIsApplicand ? subjectRefForApplicand(doc.ref) : undefined) ??
    undefined;
  const sceneRef: DocumentReference | undefined = doc.get("scene");
  const episodePath = sceneRef
    ? getEpisodePath(episodeForScene(sceneRef))
    : episodePathForApplicand(doc.ref);
  const applicands_ = useAvailableApplicands(
    applicandKind,
    applicandSubjectRef,
    episodePath ? [episodePath] : [],
    category ? [category] : [],
  );
  const applicands = applicands_?.filter(
    (applicand) => applicand.ref.path !== doc.ref.path,
  );
  const collName = applicandCollectionName(applicandKind);
  const selectedApplicands: DocumentReference[] = doc.get(collName) ?? [];
  const [isEditing, setIsEditing] = React.useState(false);
  if (!applicands) return <ProgressBar indeterminate />;
  return (
    <View style={{ gap: 4 }}>
      {!readonly && applicands.length ? (
        <View style={{ gap: 4, flexDirection: "row", alignItems: "center" }}>
          <FixtureLabel
            style={{ fontSize: 16 }}
            text={
              ph(
                parentApplicandKind === applicandKind
                  ? "based-on"
                  : `${userFacingApplicandKind(applicandKind, subjectKind === "character")}s`,
              ) as string
            }
          />
          {!readonly && (
            <Button onPress={() => setIsEditing((editing) => !editing)}>
              {ph(isEditing ? "done" : "edit") as string}
            </Button>
          )}
        </View>
      ) : null}
      {!isEditing ? (
        <View style={{ gap: 4, flexDirection: "row", flexWrap: "wrap" }}>
          {applicands
            .filter((applicand) =>
              selectedApplicands.find((ref) => ref.path === applicand.ref.path),
            )
            .map((applicand) => (
              <ApplicandChip key={applicand.id} applicand={applicand} />
            ))}
        </View>
      ) : (
        <ChipSelector
          entries={applicands.map((applicand) => ({
            id: applicand.ref.path,
            label:
              applicand.get("displayName") +
              ": " +
              (applicand.get("description") ?? ""),
          }))}
          selectedEntryIds={selectedApplicands.map((ref) => ref.path)}
          onSelectionChanged={(selectedIds) =>
            doc.ref.update({
              [collName]: applicands
                .map(({ ref }) => ref)
                .filter((ref) => selectedIds.includes(ref.path)),
            })
          }
        />
      )}
    </View>
  );
}
